import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Spinner,
  Input,
  Button,
  Switch,
  Field,
  Dropdown,
  Option,
  Text,
  Tooltip
} from '@fluentui/react-components';
import { Person, useInfinitePersons } from '#root/queries/persons';
import {
  ArrowClockwiseRegular,
  ArrowSortFilled,
  FilterRegular,
  PersonAvailableFilled,
  PersonSearchRegular,
  AddRegular,
  CheckmarkCircleFilled,
  CallRegular,
  PersonDeleteRegular,
  PeopleTeamAddRegular
} from '@fluentui/react-icons';
import { showToast } from '#root/components/GlobalToaster';
import { useMatchPersons } from '#root/queries/persons';
import { useInfiniteIncidents, useIncidentById, Incident } from '#root/queries/incidents';
import { useCurrentUser, useVAdminSelectedIncident } from '#root/queries/local';
import InfiniteDropdown from '#root/components/InfiniteDropdown';
import { useInfiniteDropdown } from '#root/hooks/useInfiniteDropdown';
import "./styles.scss";
import { PERSON_TYPES, PERSON_ENUMS } from '#root/utils/persons';
import { Breadcrumbs } from '#root/components/Breadcrumbs';
import { CompareDialog } from './CompareDialog';
import { PersonCard } from '#root/components/PersonCardContent';
import { getUnitEnumLabelByUnit } from "#root/utils/enums";
import { ChevronUpRegular } from '@fluentui/react-icons';
import { debounce } from 'lodash';

const ITEMS_PER_PAGE = 20;

interface FilterState {
  incidentId?: string | null;
  name: string;
  gender?: number;
  age?: number;
  height?: number;
  weight?: number;
  hair?: number;
  eyes?: number;
  matchByLastName: boolean;
  matchByFace: boolean;
}

const initialFilters: FilterState = {
  incidentId: undefined,
  name: '',
  gender: undefined,
  age: undefined,
  height: undefined,
  weight: undefined,
  hair: undefined,
  eyes: undefined,
  matchByLastName: true,
  matchByFace: true
};

function normalizeText(value?: string | null): string | null {
  const normalized = value?.trim().toLowerCase();
  return normalized ? normalized : null;
}

function getNormalizedFullName(person: Person): string | null {
  return normalizeText(person.fullName)
    ?? normalizeText([person.firstName, person.middleName, person.lastName].filter(Boolean).join(' '));
}

function getNormalizedLastName(person: Person): string | null {
  const explicitLastName = normalizeText(person.lastName);
  if (explicitLastName) return explicitLastName;

  const fullName = person.fullName?.trim();
  if (!fullName) return null;

  const parts = fullName.split(/\s+/).filter(Boolean);
  return normalizeText(parts[parts.length - 1]);
}

function getNormalizedFirstName(person: Person): string | null {
  const explicitFirstName = normalizeText(person.firstName);
  if (explicitFirstName) return explicitFirstName;

  const fullName = person.fullName?.trim();
  if (!fullName) return null;

  const parts = fullName.split(/\s+/).filter(Boolean);
  return normalizeText(parts[0]);
}

function compareAlphabetically(a: Person, b: Person): number {
  const lastNameDiff = (getNormalizedLastName(a) ?? '').localeCompare(getNormalizedLastName(b) ?? '', undefined, { sensitivity: 'base' });
  if (lastNameDiff !== 0) return lastNameDiff;

  const firstNameDiff = (getNormalizedFirstName(a) ?? '').localeCompare(getNormalizedFirstName(b) ?? '', undefined, { sensitivity: 'base' });
  if (firstNameDiff !== 0) return firstNameDiff;

  return (getNormalizedFullName(a) ?? '').localeCompare(getNormalizedFullName(b) ?? '', undefined, { sensitivity: 'base' });
}

function getEffectiveSortPriority(
  person: Person,
  matchByFace: boolean,
  matchByLastName: boolean
): number {
  if (matchByFace && person.sortPriority === 1) return 1;
  if (matchByLastName && (person.sortPriority === 2 || person.sortPriority === 3)) return person.sortPriority;
  return 4;
}

function comparePersons(
  a: Person,
  b: Person,
  matchByFace: boolean,
  matchByLastName: boolean
): number {
  const priorityA = getEffectiveSortPriority(a, matchByFace, matchByLastName);
  const priorityB = getEffectiveSortPriority(b, matchByFace, matchByLastName);
  const sortPriorityDiff = priorityA - priorityB;
  if (sortPriorityDiff !== 0) return sortPriorityDiff;

  if (priorityA === 1) {
    const faceMatchScoreDiff = (b.faceMatchScore ?? 0) - (a.faceMatchScore ?? 0);
    if (faceMatchScoreDiff !== 0) return faceMatchScoreDiff;

    const faceGroupIdA = normalizeText(a.faceGroupId) ?? normalizeText(a.id) ?? '';
    const faceGroupIdB = normalizeText(b.faceGroupId) ?? normalizeText(b.id) ?? '';
    const faceGroupDiff = faceGroupIdA.localeCompare(faceGroupIdB, undefined, { sensitivity: 'base' });
    if (faceGroupDiff !== 0) return faceGroupDiff;

    const isRepresentativeA = faceGroupIdA !== '' && normalizeText(a.id) === faceGroupIdA ? 0 : 1;
    const isRepresentativeB = faceGroupIdB !== '' && normalizeText(b.id) === faceGroupIdB ? 0 : 1;
    if (isRepresentativeA !== isRepresentativeB) return isRepresentativeA - isRepresentativeB;
  }

  return compareAlphabetically(a, b);
}

function getAutoGroupKey(
  person: Person,
  matchByFace: boolean,
  matchByLastName: boolean
): string {
  const faceGroupId = normalizeText(person.faceGroupId);
  if (matchByFace && faceGroupId) {
    return `face::${faceGroupId}`;
  }

  const lastName = getNormalizedLastName(person);
  if (matchByLastName && lastName) {
    return `name::${lastName}`;
  }

  return person.id!;
}


const MatchVictims: React.FC = () => {
  const { data: currentUser } = useCurrentUser();
  const unit = currentUser?.unit;
  const [selectedVictim, setSelectedVictim] = useState<Person | null>(null);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [isCompareDialogOpen, setIsCompareDialogOpen] = useState(false);
  const { selectedIncidentId: vAdminIncidentId, setVAdminSelectedIncidentId } = useVAdminSelectedIncident();

  const [filters, setFilters] = useState<FilterState>(() => ({
    ...initialFilters,
    incidentId: vAdminIncidentId || undefined
  }));

  const {
    selectedData: selectedIncidentData,
    setFilter: setIncidentFilter,
    queryResult: incidentQueryResult,
    handleChange: handleIncidentChange
  } = useInfiniteDropdown<Incident>({
    initialId: vAdminIncidentId || undefined,
    onChange: (id) => {
      const newIncidentId = id;
      setVAdminSelectedIncidentId(newIncidentId);
      setFilters(prev => ({ ...prev, incidentId: newIncidentId }));
      setSelectedVictim(null);
    },
    useInfiniteQuery: useInfiniteIncidents,
    useItemById: useIncidentById
  });

  const publicColumnRef = useRef<HTMLDivElement>(null);
  const victimsColumnRef = useRef<HTMLDivElement>(null);
  const [showMatchedVictims, setShowMatchedVictims] = useState(false);

  const isAutoGroupingEnabled = filters.matchByFace || filters.matchByLastName;

  const queryFilters = {
    name: filters.name,
    gender: filters.gender,
    age: filters.age,
    height: filters.height,
    weight: filters.weight,
    hair: filters.hair,
    eyes: filters.eyes,
    matchByLastName: filters.matchByLastName,
    matchByFace: filters.matchByFace,
    includeMatchedVictimsForFaceMatch: showMatchedVictims,
    ...(filters.incidentId ? { incidentId: filters.incidentId } : {})
  };

  const {
    data: victimsResponse,
    isLoading: victimsLoading,
    refetch: refetchVictims,
    fetchNextPage: fetchNextVictimsPage,
    isFetching: isVictimsFetching,
    hasNextPage: hasVictimsNextPage
  } = useInfinitePersons({
    type: PERSON_TYPES.VICTIM,
    isMatched: showMatchedVictims ? undefined : false,
    take: ITEMS_PER_PAGE,
    ...queryFilters
  });


  const {
    data: publicResponse,
    isLoading: publicLoading,
    refetch: refetchPublic,
    fetchNextPage: fetchNextPublicPage,
    isFetching: isPublicFetching,
    hasNextPage: hasPublicNextPage
  } = useInfinitePersons({
    isMatched: false,
    type: PERSON_TYPES.PUBLIC,
    take: ITEMS_PER_PAGE,
    ...queryFilters
  });

  const victimsData = useMemo(
    () => victimsResponse?.pages.flatMap(page => page.items) ?? [],
    [victimsResponse]
  );
  const victimsCount = victimsResponse?.pages[0]?.totalCount ?? 0;
  const publicData = useMemo(
    () => publicResponse?.pages.flatMap(page => page.items) ?? [],
    [publicResponse]
  );
  const publicCount = publicResponse?.pages[0]?.totalCount ?? 0;



  const matchPersonsMutation = useMatchPersons();

  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedPublicIds, setSelectedPublicIds] = useState<Set<string>>(new Set());

  const filteredVictimsData = showMatchedVictims
    ? victimsData
    : victimsData.filter(p => p.isMatched !== true);

  const sortedVictimsData = useMemo(() => {
    if (!filters.matchByFace && !filters.matchByLastName) return filteredVictimsData;

    return [...filteredVictimsData].sort((a, b) =>
      comparePersons(a, b, filters.matchByFace, filters.matchByLastName)
    );
  }, [filteredVictimsData, filters.matchByFace, filters.matchByLastName]);




  const [manuallyUngrouped, setManuallyUngrouped] = useState<Set<string>>(new Set());

  const [manualGroupAssignments, setManualGroupAssignments] = useState<Map<string, string>>(new Map());

  const isLoading = victimsLoading || publicLoading;

  const handleRefresh = () => {
    refetchPublic();
    refetchVictims();
  };

  useEffect(() => {
    refetchPublic();
    refetchVictims();
  }, [filters, refetchPublic, refetchVictims]);

  useEffect(() => {
    const el = publicColumnRef.current;
    if (!el) return;

    const checkAndLoadMore = () => {
      const threshold = 800;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        if (hasPublicNextPage && !isPublicFetching && !publicLoading) {
          fetchNextPublicPage();
        }
      }
    };

    checkAndLoadMore();
    const onScroll = debounce(checkAndLoadMore, 100);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasPublicNextPage, isPublicFetching, publicLoading, fetchNextPublicPage]);

  useEffect(() => {
    const el = victimsColumnRef.current;
    if (!el) return;

    const checkAndLoadMore = () => {
      const threshold = 800;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        if (hasVictimsNextPage && !isVictimsFetching && !victimsLoading) {
          fetchNextVictimsPage();
        }
      }
    };

    checkAndLoadMore();
    const onScroll = debounce(checkAndLoadMore, 100);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasVictimsNextPage, isVictimsFetching, victimsLoading, fetchNextVictimsPage]);

  const groupedPublicData = useMemo(() => {
    const groups: Record<string, Person[]> = {};


    const placed = new Set<string>();

    publicData.forEach(person => {
      const pid = person.id!;
      if (placed.has(pid)) return;


      if (manuallyUngrouped.has(pid)) {
        const soloKey = `__ungrouped__${pid}`;
        groups[soloKey] = [person];
        placed.add(pid);
        return;
      }


      const manualKey = manualGroupAssignments.get(pid);
      if (manualKey) {
        groups[manualKey] ??= [];
        groups[manualKey].push(person);
        placed.add(pid);
        return;
      }


      if (isAutoGroupingEnabled) {
        const key = getAutoGroupKey(person, filters.matchByFace, filters.matchByLastName);
        groups[key] ??= [];
        groups[key].push(person);
        placed.add(pid);
      } else {

        groups[pid] = [person];
        placed.add(pid);
      }
    });

    return Object.fromEntries(
      Object.entries(groups).map(([key, persons]) => [
        key,
        [...persons].sort((a, b) =>
          comparePersons(a, b, filters.matchByFace, filters.matchByLastName)
        )
      ])
    );
  }, [manualGroupAssignments, manuallyUngrouped, publicData, filters.matchByFace, filters.matchByLastName, isAutoGroupingEnabled]);

  const orderedPublicGroups = useMemo(() => {
    const victimFaceGroupIdSet = new Set(
      filteredVictimsData
        .map(person => normalizeText(person.faceGroupId) ?? normalizeText(person.id))
        .filter((faceGroupId): faceGroupId is string => Boolean(faceGroupId))
    );

    return Object.entries(groupedPublicData)
      .map(([groupKey, persons], index) => {
        const cover = persons[0];
        const coverPriority = cover
          ? getEffectiveSortPriority(cover, filters.matchByFace, filters.matchByLastName)
          : 4;
        const faceGroupId = coverPriority === 1 ? normalizeText(cover?.faceGroupId) ?? undefined : undefined;
        const hasVictimFaceMatch = Boolean(faceGroupId && victimFaceGroupIdSet.has(faceGroupId));
        const bestFaceScore = Math.max(0, ...persons.map(person => person.faceMatchScore ?? 0));

        const syncPriority = hasVictimFaceMatch
          ? 1
          : coverPriority === 1
            ? 2
            : coverPriority === 2
              ? 3
              : coverPriority === 3
                ? 4
                : 5;

        return {
          groupKey,
          persons,
          index,
          coverPriority,
          faceGroupId,
          hasVictimFaceMatch,
          syncPriority,
          bestFaceScore,
          sortLastName: cover ? getNormalizedLastName(cover) ?? '' : '',
          sortFirstName: cover ? getNormalizedFirstName(cover) ?? '' : '',
          sortFullName: cover ? getNormalizedFullName(cover) ?? '' : ''
        };
      })
      .sort((a, b) => {
        if (a.syncPriority !== b.syncPriority) return a.syncPriority - b.syncPriority;

        if ((a.syncPriority === 1 || a.syncPriority === 2) && a.bestFaceScore !== b.bestFaceScore) {
          return b.bestFaceScore - a.bestFaceScore;
        }

        const lastNameDiff = a.sortLastName.localeCompare(b.sortLastName, undefined, { sensitivity: 'base' });
        if (lastNameDiff !== 0) return lastNameDiff;

        const firstNameDiff = a.sortFirstName.localeCompare(b.sortFirstName, undefined, { sensitivity: 'base' });
        if (firstNameDiff !== 0) return firstNameDiff;

        const fullNameDiff = a.sortFullName.localeCompare(b.sortFullName, undefined, { sensitivity: 'base' });
        if (fullNameDiff !== 0) return fullNameDiff;

        return a.index - b.index;
      });
  }, [filteredVictimsData, filters.matchByFace, filters.matchByLastName, groupedPublicData]);

  // Reorder victims so they align horizontally with their matching public
  // stacks — by face match and/or by last name.
  const syncedVictimsData = useMemo(() => {
    if (!filters.matchByFace && !filters.matchByLastName) return sortedVictimsData;

    const ordered: Person[] = [];
    const placed = new Set<string>();
    const victimsByFaceGroupId = new Map<string, Person[]>();

    for (const victim of sortedVictimsData) {
      const faceGroupId = normalizeText(victim.faceGroupId);
      if (filters.matchByFace && faceGroupId) {
        if (!victimsByFaceGroupId.has(faceGroupId)) victimsByFaceGroupId.set(faceGroupId, []);
        victimsByFaceGroupId.get(faceGroupId)!.push(victim);
      }
    }

    const victimsByLastName = new Map<string, Person[]>();
    for (const v of sortedVictimsData) {
      const lastName = getNormalizedLastName(v);
      if (lastName) {
        if (!victimsByLastName.has(lastName)) victimsByLastName.set(lastName, []);
        victimsByLastName.get(lastName)!.push(v);
      }
    }

    for (const { persons, coverPriority, faceGroupId, hasVictimFaceMatch } of orderedPublicGroups) {
      const cover = persons[0];

      if (filters.matchByFace && coverPriority === 1 && hasVictimFaceMatch && faceGroupId) {
        const faceGroupVictims = victimsByFaceGroupId.get(faceGroupId) ?? [];
        for (const victim of faceGroupVictims) {
          const victimId = victim.id?.toLowerCase() ?? '';
          if (victimId !== '' && !placed.has(victimId)) {
            ordered.push(victim);
            placed.add(victimId);
          }
        }
        continue;
      }

      if (filters.matchByLastName && (coverPriority === 2 || coverPriority === 3)) {
        const lastName = getNormalizedLastName(cover);
        if (lastName && victimsByLastName.has(lastName)) {
          for (const v of victimsByLastName.get(lastName)!) {
            const vid = v.id?.toLowerCase() ?? '';
            if (!placed.has(vid)) {
              ordered.push(v);
              placed.add(vid);
            }
          }
        }
        continue;
      }
    }

    // Append remaining victims that had no matching public group,
    // sorted alphabetically so already-matched victims (which keep their
    // backend sortPriority) don't push ahead of unmatched ones.
    const remaining = sortedVictimsData
      .filter(v => !placed.has(v.id?.toLowerCase() ?? ''));
    remaining.sort(compareAlphabetically);
    for (const v of remaining) {
      ordered.push(v);
    }

    return ordered;
  }, [filters.matchByFace, filters.matchByLastName, orderedPublicGroups, sortedVictimsData]);

  const handleUngroupPerson = useCallback((personId: string) => {
    setManuallyUngrouped(prev => {
      const next = new Set(prev);
      next.add(personId);
      return next;
    });
    setManualGroupAssignments(prev => {
      const next = new Map(prev);
      next.delete(personId);
      return next;
    });


    setSelectedPublicIds(prev => {
      const next = new Set(prev);
      next.delete(personId);
      return next;
    });
  }, []);

  const handleAddToGroup = useCallback((personId: string, targetGroupKey?: string) => {
    setManuallyUngrouped(prev => {
      const next = new Set(prev);
      next.delete(personId);
      return next;
    });

    if (targetGroupKey) {
      setManualGroupAssignments(prev => {
        const next = new Map(prev);
        next.set(personId, targetGroupKey);
        return next;
      });
    } else {

      setManualGroupAssignments(prev => {
        const next = new Map(prev);
        next.delete(personId);
        return next;
      });
    }
  }, []);


  const handleRestoreToAutoGroup = useCallback((personId: string) => {
    handleAddToGroup(personId);
  }, [handleAddToGroup]);

  const handleGroupSelected = useCallback(() => {
    if (selectedPublicIds.size < 2) return;
    const newGroupKey = `__manual__${Date.now()}`;
    selectedPublicIds.forEach(id => {
      handleAddToGroup(id, newGroupKey);
    });
  }, [selectedPublicIds, handleAddToGroup]);

  const handleMatch = useCallback(async (publicIds: string[], victimId: string) => {
    try {
      await matchPersonsMutation.mutateAsync({
        publicIds,
        victimId
      });

      showToast("Victims matched successfully", 'success');

      // Clear only the matched public selections, keeping others so the dialog
      // can continue comparing any remaining unmatched public requests.
      setSelectedPublicIds(prev => {
        const next = new Set(prev);
        publicIds.forEach(id => next.delete(id));
        return next;
      });

      // Clean up manual grouping state for the matched public persons only.
      setManuallyUngrouped(prev => {
        const next = new Set(prev);
        publicIds.forEach(id => next.delete(id));
        return next;
      });
      setManualGroupAssignments(prev => {
        const next = new Map(prev);
        publicIds.forEach(id => next.delete(id));
        return next;
      });

      // Refresh public list to remove matched public cards.
      // Also refetch victims so the victim card gets the updated matchedCount
      // from the server (the Matched (X) badge reflects the true total).
      await Promise.all([refetchPublic(), refetchVictims()]);
    } catch (error) {
      showToast("Failed to match persons", 'error');
    }
  }, [matchPersonsMutation, refetchPublic, refetchVictims]);


  const toggleGroup = (name: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
        setActiveGroupId(null);
      } else {
        next.add(name);
        setActiveGroupId(name);
      }
      return next;
    });
  };

  const togglePublicSelection = (id: string) => {
    setSelectedPublicIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectGroup = (ids: string[]) => {
    setSelectedPublicIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  };


  const hasSelectedInGroup = useMemo(() => {
    if (selectedPublicIds.size === 0) return false;
    return orderedPublicGroups.some(
      ({ groupKey: key, persons }) => persons.length > 1 && !key.startsWith('__ungrouped__') && persons.some(p => p.id && selectedPublicIds.has(p.id))
    );
  }, [orderedPublicGroups, selectedPublicIds]);

  const renderEmptyState = () => (
    <div className="match-victims__empty-state">
      <PersonSearchRegular className="match-victims__empty-state-icon" />
      <Text size={400}>No victims found</Text>
      <Text size={300}>Try adjusting your filters or search criteria</Text>
    </div>
  );

  const renderPersonCard = (
    person: Person,
    isSelected: boolean,
    options?: {
      showRestoreButton?: boolean;
      showMatchedBadge?: boolean;
      matchedCount?: number;
      isConnected?: boolean;
      groupKey?: string;
    }
  ) => {
    const isMatched = options?.showMatchedBadge;
    const isContacted = options?.isConnected;
    const matchedCount = options?.matchedCount;

    return (
      <div className={`large-card person-card card-clickable ${isSelected ? 'large-card--selected' : ''} ${isMatched ? 'large-card--matched' : ''} ${(isMatched || isContacted) ? 'large-card--has-badges' : ''}`}>
        {/* Status badges — top-right corner inside the card */}
        {(isMatched || isContacted) && (
          <div className="person-card__status-badges">
            {isMatched && (
              <Tooltip content="Victim has been matched" relationship="label">
                <div className="person-card__status-badge person-card__status-badge--matched">
                  <CheckmarkCircleFilled className="person-card__status-badge-icon" />
                  <span>{matchedCount != null ? `Matched (${matchedCount})` : 'Matched'}</span>
                </div>
              </Tooltip>
            )}
            {isContacted && (
              <Tooltip content="Family has been contacted" relationship="label">
                <div className="person-card__status-badge person-card__status-badge--contacted">
                  <CallRegular className="person-card__status-badge-icon" />
                  <span>Contacted</span>
                </div>
              </Tooltip>
            )}
          </div>
        )}

        <PersonCard
          person={person}
          unit={unit}
        />


        {options?.showRestoreButton && isAutoGroupingEnabled && (
          <Tooltip content="Restore to auto-group" relationship="label">
            <Button
              className="person-card__restore-btn"
              size="small"
              appearance="subtle"
              icon={<AddRegular />}
              onClick={(e) => {
                e.stopPropagation();
                person.id && handleRestoreToAutoGroup(person.id);
              }}
            />
          </Tooltip>
        )}
      </div>
    );
  };

  const renderPublicList = () => {
    if (!groupedPublicData) return null;

    return orderedPublicGroups.map(({ groupKey, persons }) => {
      const isUngroupedSolo = groupKey.startsWith('__ungrouped__');
      const isExpanded = expandedGroups.has(groupKey);
      const count = persons.length;
      const topPerson = persons[0];

      if (count === 1 || isUngroupedSolo) {
        const person = persons[0];
        const isManuallyUngrouped = manuallyUngrouped.has(person.id!);

        return (
          <div
            key={groupKey}
            onClick={() => person.id && togglePublicSelection(person.id)}
            className={selectedPublicIds.has(person.id || '') ? 'person-card--selected-multi' : ''}
          >
            {renderPersonCard(person, selectedPublicIds.has(person.id || ''), {
              showRestoreButton: isManuallyUngrouped
            })}
          </div>
        );
      }

      if (!isExpanded) {
        return (
          <div
            key={groupKey}
            className={`public-group-stack ${activeGroupId === groupKey ? 'public-group--active' : ''}`}
            onClick={() => {
              setActiveGroupId(groupKey);
              toggleGroup(groupKey);
            }}
          >
            <div className="public-group-badge">
              <Text size={200}>Public Requests ({count})</Text>
            </div>

            <div className="stack-effect">
              <div className="stack-layer stack-layer-1" />
              <div className="stack-layer stack-layer-2" />
              {renderPersonCard(topPerson, false)}
            </div>
          </div>
        );
      }

      return (
        <div
          key={groupKey}
          className={`public-group public-group--expanded ${activeGroupId === groupKey ? 'public-group--active' : ''}`}
        >
          <div className="public-group-header">
            <Button
              size="small"
              appearance="primary"
              onClick={() =>
                selectGroup(persons.map(p => p.id!).filter(Boolean))
              }
            >
              Select Group
            </Button>

            <Button
              size="small"
              appearance="subtle"
              icon={<ChevronUpRegular />}
              onClick={() => toggleGroup(groupKey)}
            />
          </div>

          <div className="public-group-list">
            {persons.map(person => (
              <div
                key={person.id}
                onClick={(e) => {
                  e.stopPropagation();
                  person.id && togglePublicSelection(person.id);
                }}
                className={selectedPublicIds.has(person.id || '') ? 'person-card--selected-multi' : ''}
              >
                {renderPersonCard(person, selectedPublicIds.has(person.id || ''), {
                  groupKey
                })}
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="match-victims-container layout__body--paddings">
      <div className="match-victims">
        <div className="emc-page-header">
          <div className="emc-page-header__top-row">
            <div className="emc-page-header__breadcrumbs">
              <Breadcrumbs />
            </div>
            <div className="emc-page-header__props">
              <Field>
                <Switch
                  label="AI Match Faces"
                  checked={filters.matchByFace}
                  onChange={(_, data) => setFilters(prev => ({ ...prev, matchByFace: data.checked }))}
                />
              </Field>
              <Field>
                <Switch
                  label="Match by Last Name"
                  checked={filters.matchByLastName}
                  onChange={(_, data) => setFilters(prev => ({ ...prev, matchByLastName: data.checked }))}
                />
              </Field>
              <Button
                appearance="primary"
                shape="circular"
                icon={<ArrowClockwiseRegular />}
                onClick={handleRefresh}
              />
            </div>
          </div>
          <div className="emc-page-header__title">
            <h2>
              Match Victims <div className="divider" />
              <Button
                appearance={isFiltersCollapsed ? 'outline' : 'primary'}
                icon={<FilterRegular />}
                onClick={() => setIsFiltersCollapsed(prev => !prev)}
              />
            </h2>
          </div>
        </div>

        <div className={`match-victims__filters ${isFiltersCollapsed ? 'match-victims__filters-collapsed' : ''}`}>
          <Field label="Incident" required className="match-victims__filters-incident">
            <InfiniteDropdown
              value={filters.incidentId || ''}
              onChange={(_, data) => handleIncidentChange(data.value)}
              onFilterChange={setIncidentFilter}
              queryResult={incidentQueryResult}
              itemToOption={(incident) => ({
                value: incident.id ?? '',
                text: incident.name ?? ''
              })}
              placeholder="All Incidents"
              selectedItem={selectedIncidentData}
              clearable
            />
          </Field>

          <Field label="Name" className="match-victims__filters-name">
            <Input
              value={filters.name}
              onChange={(_, data) => setFilters(prev => ({ ...prev, name: data.value }))}
            />
          </Field>

          <Field label="Gender" className="match-victims__filters-gender">
            <Dropdown
              clearable
              value={filters.gender?.toString()}
              onOptionSelect={(_, data) =>
                setFilters(prev => ({ ...prev, gender: data.optionValue ? parseInt(data.optionValue) : undefined }))
              }
            >
              {PERSON_ENUMS.GENDER.map(gender => (
                <Option key={gender.value} value={gender.value.toString()}>
                  {gender.label}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Age" className="match-victims__filters-age">
            <Dropdown
              clearable
              value={filters.age?.toString()}
              onOptionSelect={(_, data) =>
                setFilters(prev => ({ ...prev, age: data.optionValue ? parseInt(data.optionValue) : undefined }))
              }
            >
              {PERSON_ENUMS.AGE_RANGES.map(age => (
                <Option key={age.value} value={age.value.toString()}>
                  {getUnitEnumLabelByUnit(age, unit)}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Height" className="match-victims__filters-height">
            <Dropdown
              clearable
              value={filters.height?.toString()}
              onOptionSelect={(_, data) =>
                setFilters(prev => ({ ...prev, height: data.optionValue ? parseInt(data.optionValue) : undefined }))
              }
            >
              {PERSON_ENUMS.HEIGHT_RANGES.map(height => (
                <Option key={height.value} value={height.value.toString()}>
                  {getUnitEnumLabelByUnit(height, unit)}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Weight" className="match-victims__filters-weight">
            <Dropdown
              clearable
              value={filters.weight?.toString()}
              onOptionSelect={(_, data) =>
                setFilters(prev => ({ ...prev, weight: data.optionValue ? parseInt(data.optionValue) : undefined }))
              }
            >
              {PERSON_ENUMS.WEIGHT_RANGES.map(weight => (
                <Option key={weight.value} value={weight.value.toString()}>
                  {getUnitEnumLabelByUnit(weight, unit)}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Hair" className="match-victims__filters-hair">
            <Dropdown
              clearable
              value={filters.hair?.toString()}
              onOptionSelect={(_, data) =>
                setFilters(prev => ({ ...prev, hair: data.optionValue ? parseInt(data.optionValue) : undefined }))
              }
            >
              {PERSON_ENUMS.HAIR_COLORS.map(hair => (
                <Option key={hair.value} value={hair.value.toString()}>
                  {hair.label}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Eyes" className="match-victims__filters-eyes">
            <Dropdown
              clearable
              value={filters.eyes?.toString()}
              onOptionSelect={(_, data) =>
                setFilters(prev => ({ ...prev, eyes: data.optionValue ? parseInt(data.optionValue) : undefined }))
              }
            >
              {PERSON_ENUMS.EYE_COLORS.map(eyes => (
                <Option key={eyes.value} value={eyes.value.toString()}>
                  {eyes.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>

        {isLoading ? (
          <div className="match-victims__loading">
            <Spinner size="medium" />
          </div>
        ) : (!filters.incidentId && publicCount === 0 && victimsCount === 0) ? (
          renderEmptyState()
        ) : (
          <div className="match-victims__columns-container">
            <div className="match-victims__column">
              <h3>Public ({publicCount}) <div className="divider" /></h3>
              <div className="match-victims__column-content" ref={publicColumnRef}>
                {renderPublicList()}
                {isPublicFetching && !isLoading && (
                  <div className="match-victims__column-loading">
                    <Spinner size="tiny" />
                  </div>
                )}
              </div>
            </div>

            <div className="match-victims__column">
              <h3>
                Victims ({syncedVictimsData.length}) <div className="divider" />
                <Field>
                  <Switch
                    label="Show Matched"
                    checked={showMatchedVictims}
                    onChange={(_, data) => setShowMatchedVictims(data.checked)}
                  />
                </Field>
              </h3>
              <div className="match-victims__column-content" ref={victimsColumnRef}>
                {syncedVictimsData.map(person => (
                  <div key={person.id} onClick={() => setSelectedVictim(prev => prev?.id === person.id ? null : person)}>
                    {renderPersonCard(person, selectedVictim?.id === person.id, {
                      showMatchedBadge: person.isMatched === true && (person.matchedCount ?? 0) > 0,
                      matchedCount: person.matchedCount,
                      isConnected: person.isConnected === true
                    })}
                  </div>
                ))}
                {isVictimsFetching && !isLoading && (
                  <div className="match-victims__column-loading">
                    <Spinner size="tiny" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {(selectedPublicIds.size > 0 || selectedVictim) && (
          <div className="emc-page-footer">
            <div className="group-actions">
              {hasSelectedInGroup && (
                <Button
                  className="remove-from-group"
                  icon={<PersonDeleteRegular />}
                  appearance="primary"
                  onClick={() => {
                    selectedPublicIds.forEach(id => handleUngroupPerson(id));
                  }}
                >
                  Remove from group
                </Button>
              )}
              {selectedPublicIds.size >= 2 && (
                <Button
                  icon={<PeopleTeamAddRegular />}
                  appearance="primary"
                  onClick={handleGroupSelected}
                >
                  Group Selected
                </Button>
              )}
            </div>
            <Button className='match-selected-button'
              icon={<ArrowSortFilled />}
              appearance="primary"
              disabled={selectedPublicIds.size === 0 || !selectedVictim || matchPersonsMutation.isPending}
              onClick={() => setIsCompareDialogOpen(true)}
            >
              Compare Selected
            </Button>
            <Button
              icon={matchPersonsMutation.isPending ? <Spinner size="tiny" /> : <PersonAvailableFilled />}
              className="match-selected"
              appearance="primary"
              disabled={selectedPublicIds.size === 0 || !selectedVictim || matchPersonsMutation.isPending}
              onClick={() => {
                const publicIds = Array.from(selectedPublicIds);
                handleMatch(publicIds, selectedVictim?.id ?? '');
              }}
            >
              {matchPersonsMutation.isPending ? 'Matching...' : 'Match Selected'}
            </Button>
          </div>
        )}

        {selectedPublicIds.size > 0 && selectedVictim && (
          <CompareDialog
            open={isCompareDialogOpen}
            onOpenChange={setIsCompareDialogOpen}
            publicPersons={publicData.filter(p => p.id && selectedPublicIds.has(p.id))}
            victimId={selectedVictim.id}
            onMatchSelected={(matchedIds) => {
              return handleMatch(matchedIds, selectedVictim.id ?? '');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MatchVictims;
